import React from 'react'
import { useTranslation } from 'next-i18next'
import NextLink from 'next/link'
import NextImage from 'next/image'
import { EmtGridContainer, EmtIcon, EmtTypography } from 'emt-components-v2'
import { NewsCard, NewsCardButton } from '@/components/Home'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import { Routes } from '@/assets/configs'
import VariantImages from '@/assets/VariantImages'
import Blur from '@/assets/img/blur.png'
import { getInsurancePrefix, getPartnerLogoImage } from '@/data'
import { NotificationCards } from '@/components/Home/NotificationCards'
import useCurrentUser from '@/hooks/useCurrentUser'
import { usePetsittingMode } from '@/store/usePetsittingMode'

interface NotificationCardsViewProps {
  totalCards: number
  isPremium: boolean
  isCertified: boolean
  petSitterAllowed?: boolean
  favoritesCount: number
  unreadMessagesCount: number
  trialDurationDays?: number | null
  insurance: any
  settings: any
  showSmsFeature: boolean
  queryType: string | undefined
  appendQueryType: (path: string) => string
}

export const NotificationCardsView: React.FC<NotificationCardsViewProps> = ({
  totalCards,
  isPremium,
  isCertified,
  petSitterAllowed,
  favoritesCount,
  unreadMessagesCount,
  trialDurationDays,
  insurance,
  settings,
  showSmsFeature,
  queryType,
  appendQueryType,
}) => {
  const { t } = useTranslation('home')
  const { tendersEnabled } = useCurrentUser()
  const { isPetsittingMode } = usePetsittingMode()

  return (
    <>
      {/* {!!totalCards && ( */}
      <EmtGridContainer>
        <NotificationCards totalCards={totalCards} />

        {/* Premium Card */}
        {!isPremium && (
          <Grid
            item
            xs={12}
            md={6}
            lg={totalCards > 3 ? 3 : 4}
            sx={{
              display: 'flex',
            }}
          >
            <NewsCard elevation={0}>
              <EmtIcon className="emt-crown-1" />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ marginBottom: '5px' }}>
                  <EmtTypography variant="body1" fontWeight="bold">
                    {isPetsittingMode ? t('header.news.becomePremiumSitting') : t('header.news.becomePremium')}
                  </EmtTypography>
                  <EmtTypography variant="body2" color="greyGradient.250">
                    {isPetsittingMode
                      ? t(`header.news.subtitle.${queryType}Sitting`)
                      : t(`header.news.subtitle.${queryType}`)}
                  </EmtTypography>
                </Box>

                <NextLink legacyBehavior href={appendQueryType(Routes.Premium.Base)} passHref>
                  <NewsCardButton size="small" color="secondary">
                    {t(`header.news.premiumButton.${!!trialDurationDays ? 'trial' : 'noTrial'}`)}
                  </NewsCardButton>
                </NextLink>
              </Box>
            </NewsCard>
          </Grid>
        )}
        {/* Premium Card */}

        {/* Sitter Card */}
        {petSitterAllowed && isPetsittingMode && (
          <Grid
            item
            xs={12}
            md={6}
            lg={totalCards > 3 ? 3 : 4}
            sx={{
              display: 'flex',
            }}
          >
            <NewsCard elevation={0}>
              <NextImage src={VariantImages.SuperSittersPets} width={58} alt={'sitter-dog'} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ marginBottom: '5px' }}>
                  <EmtTypography variant="body1" fontWeight="bold">
                    {t('header.news.petsitting.title')}
                  </EmtTypography>
                  <EmtTypography variant="body2" color="greyGradient.250">
                    {t('header.news.petsitting.subtitle')}
                  </EmtTypography>
                </Box>
                <NextLink
                  legacyBehavior
                  href={tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Base}
                  passHref
                >
                  <NewsCardButton color="purple" variant="outlined" size="small">
                    {t('header.news.petsitting.button')}
                  </NewsCardButton>
                </NextLink>
              </Box>
            </NewsCard>
          </Grid>
        )}
        {/* Sitter Card */}

        {/* Favorite receive Card */}
        {favoritesCount ? (
          <Grid
            item
            xs={12}
            md={6}
            lg={totalCards > 3 ? 3 : 4}
            sx={{
              display: 'flex',
            }}
          >
            <NewsCard elevation={0}>
              <EmtIcon className="emt-heart-1" />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ marginBottom: '5px' }}>
                  <EmtTypography variant="body1" fontWeight="bold">
                    {t('header.news.favoriteReceive', { count: favoritesCount })}
                  </EmtTypography>
                </Box>

                <NextLink href={appendQueryType(Routes.Favorites._pathname)} passHref>
                  <NewsCardButton variant="outlined" size="small" color="primary">
                    {t('header.news.consult')}
                  </NewsCardButton>
                </NextLink>
              </Box>
            </NewsCard>
          </Grid>
        ) : null}
        {/* END Favorite receive Card */}

        {/* New Message Card */}
        {!!unreadMessagesCount && (
          <Grid
            item
            xs={12}
            md={6}
            lg={totalCards > 3 ? 3 : 4}
            sx={{
              display: 'flex',
            }}
          >
            <NewsCard elevation={0}>
              <EmtIcon className="emt-message-text-1" sx={{ fontSize: '24px', fontWeight: '200 !important' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ marginBottom: '5px' }}>
                  <EmtTypography variant="body1" fontWeight="bold">
                    {t('header.news.newMessage', { count: unreadMessagesCount })}
                  </EmtTypography>
                </Box>
                <NextLink legacyBehavior href={appendQueryType(Routes.Message._pathname)} passHref>
                  <NewsCardButton variant="outlined" size="small" color="primary">
                    {t('header.news.read')}
                  </NewsCardButton>
                </NextLink>
              </Box>
            </NewsCard>
          </Grid>
        )}
        {/* New Message Card */}

        {/* Certified Card */}
        {isPremium && !isCertified && !isPetsittingMode && (
          <Grid
            item
            xs={12}
            md={6}
            lg={totalCards > 3 ? 3 : 4}
            sx={{
              display: 'flex',
            }}
          >
            <NewsCard elevation={0}>
              <EmtIcon className="emt-face-id" />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ marginBottom: '5px' }}>
                  <EmtTypography variant="body1" fontWeight="bold">
                    {t(`header.news.certificateTitle.${queryType}`)}
                  </EmtTypography>
                </Box>
                <NextLink legacyBehavior href={appendQueryType(Routes.IDCheck.Base)} passHref>
                  <NewsCardButton variant="outlined" size="small" color="success">
                    {t('header.news.certifyMyProfil')}
                  </NewsCardButton>
                </NextLink>
              </Box>
            </NewsCard>
          </Grid>
        )}
        {/* END Certified Card */}

        {/* Insurance Premium Card */}
        {insurance && (
          <Grid
            item
            xs={12}
            md={6}
            lg={totalCards > 3 ? 3 : 4}
            sx={{
              display: 'flex',
            }}
          >
            <NewsCard elevation={0}>
              <EmtIcon className="emt-crown-1" />
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ marginBottom: '5px' }}>
                  <EmtTypography variant="body1" fontWeight="bold">
                    {t('header.news.insurance.premium')}
                  </EmtTypography>
                </Box>
                <EmtTypography variant="body2" color="greyGradient.250">
                  {t('header.news.insurance.offerFormat', {
                    prefix: getInsurancePrefix(insurance),
                    name: insurance.name,
                  })}
                </EmtTypography>
              </Box>
              <Box sx={{ position: 'relative', width: '90px', height: '60px' }}>
                <NextImage
                  placeholder="blur"
                  blurDataURL={Blur.blurDataURL}
                  loader={() => getPartnerLogoImage(insurance) as string}
                  src={getPartnerLogoImage(insurance) as string}
                  quality={70}
                  layout="fill"
                  objectFit={'contain'}
                  alt={''}
                />
              </Box>
            </NewsCard>
          </Grid>
        )}
        {/* Insurance Premium Card */}

        {/* SmsFeature Card */}
        {!settings?.communicationPreferences?.receiveMessageNotificationSMS && showSmsFeature ? (
          <Grid
            item
            xs={12}
            md={6}
            lg={totalCards > 3 ? 3 : 4}
            sx={{
              display: 'flex',
            }}
          >
            <NewsCard elevation={0}>
              <EmtIcon className="emt-paw-1" />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ marginBottom: '5px' }}>
                  <EmtTypography variant="body1" fontWeight="bold">
                    {t('header.news.smsFeature.title')}
                  </EmtTypography>
                  <EmtTypography variant="body2" color="greyGradient.250">
                    {t(`header.news.smsFeature.subtitle`)}
                  </EmtTypography>
                </Box>

                <NextLink legacyBehavior href={appendQueryType(Routes.MyAccount.settings)} passHref>
                  <NewsCardButton size="small" color="primary" variant="outlined">
                    {t(`header.news.smsFeature.button`)}
                  </NewsCardButton>
                </NextLink>
              </Box>
            </NewsCard>
          </Grid>
        ) : null}
        {/* SmsFeature Card */}
      </EmtGridContainer>
      {/* )} */}
    </>
  )
}
